/*
 * (c) Copyright, 2025, Medidata Solutions Worldwide, Inc. All rights reserved.
 */

import React from 'react'
import { useServiceContext } from '../../../root'
import { RcmTable } from '../../../components/rcm-table/RcmTable'
import { TableHeadRow, TableHeadCell, TableRow } from '../../../components/table/TableComponents'
import type { Watermark } from '../../../services/api/watermarks'
import { createDateTimeFormatter } from '../../../utils/commonColumns'
import TableMenu from './TableMenu'

interface WatermarksTableProps {
  onEditWatermark: (watermark: Watermark) => void
  onDeleteSuccess?: () => void
  onExportStart?: () => void
  onExportEnd?: () => void
}

const WatermarksTable: React.FC<WatermarksTableProps> = ({
  onEditWatermark,
  onDeleteSuccess,
  onExportStart,
  onExportEnd
}) => {
  const context = useServiceContext()
  const { translate } = context.service.filter

  const formatDateTime = createDateTimeFormatter(context.service.auth, translate)

  return (
    <div>
      <RcmTable
        className="chita-table"
        noResultsText="NO_RESULTS_FILTERED"
        HeadRow={
          <TableHeadRow>
            <TableHeadCell id="watermark-name-header" sort="NAME" style={{ fontSize: '12px' }}>
              {translate('NAME')}
            </TableHeadCell>
            <TableHeadCell id="f530e6b2-3d8b-4ba6-b2a8-62ab5da65bf3" style={{ fontSize: '12px' }}>
              {translate('DESCRIPTION')}
            </TableHeadCell>
            <TableHeadCell id="watermark-created-at-header" sort="CREATED_AT" style={{ fontSize: '12px' }}>
              {translate('CREATED_ON')}
            </TableHeadCell>
            <TableHeadCell id="watermark-created-by-header" sort="CREATED_BY" style={{ fontSize: '12px' }}>
              {translate('CREATED_BY')}
            </TableHeadCell>
            <TableHeadCell id="watermark-updated-at-header" sort="UPDATED_AT" style={{ fontSize: '12px' }}>
              {translate('TABLE_TH_MODIFIED_ON')}
            </TableHeadCell>
            <TableHeadCell id="watermark-updated-by-header" sort="UPDATED_BY" style={{ fontSize: '12px' }}>
              {translate('TABLE_TH_MODIFIED_BY')}
            </TableHeadCell>
            <TableHeadCell id="20eed7af-57ce-4762-a342-4eba4252fbf6" className="actions" style={{ fontSize: '12px' }}>
              {translate('ACTIONS')}
            </TableHeadCell>
          </TableHeadRow>
        }
        Row={({ row }: { row: Watermark }) => {
          const watermark = row

          return (
            <TableRow key={watermark.id}>
              <td className="clipped-cell">
                <div className="ellipsis">
                  {watermark.system ? (
                    watermark.name
                  ) : (
                    <span
                      className="btn-link"
                      onClick={() => {
                        onEditWatermark(watermark)
                      }}
                    >
                      {watermark.name}
                    </span>
                  )}
                </div>
              </td>
              <td>{watermark.description || translate('NOT_AVAILABLE')}</td>
              <td>{formatDateTime(watermark.createdAt)}</td>
              <td>
                {watermark.createdBy && watermark.createdBy.name
                  ? watermark.createdBy.name
                  : translate('NOT_AVAILABLE')}
              </td>
              <td>{formatDateTime(watermark.updatedAt)}</td>
              <td>
                {watermark.updatedBy && watermark.updatedBy.name
                  ? watermark.updatedBy.name
                  : translate('NOT_AVAILABLE')}
              </td>
              <td className="actions">
                <TableMenu
                  item={watermark}
                  onEditWatermark={onEditWatermark}
                  onDeleteSuccess={onDeleteSuccess}
                  onExportStart={onExportStart}
                  onExportEnd={onExportEnd}
                />
              </td>
            </TableRow>
          )
        }}
      />
    </div>
  )
}

export default WatermarksTable
